import * as firebase from 'firebase';
import _set from 'lodash/set';
import {
  generateCode
} from '../Codes';
import config from '../../config/core.json';

// Initialize Firebase
firebase.initializeApp(config.firebaseDetails);
const db = firebase.database();
const storage = firebase.storage();
let stagRef;

const getCurrentUser = () => {
  return firebase.auth().currentUser;
};

const getCurrentUserId = () => {
  const user = getCurrentUser();
  return user ? user.uid : null;
};

const getStagSnapshots = (stagIds = []) => {
  const fetches = [], stags = {};
  if ( stagIds.length > 0 ) {
    stagIds.forEach(stagId => {
      fetches.push(
        db.ref('stags').child(stagId).once('value')
      );
    });
    return Promise.all(fetches).then(snapshots => {
      snapshots.forEach(snapshot => {
        const value = snapshot.val();
        const stagId = snapshot.key;
        if ( value ) {
          stags[stagId] = {
            ...stags[stagId],
            title: value && value.homepage ? value.homepage.title : null,
          };
        } else {
          delete(stags[stagId]);
        }
      });
      return stags;
    });
  } else {
    return Promise.resolve([]);
  }
};

// API methods

export const setSelectedStag = (stag) => {
  if ( stag ) {
    stagRef = db.ref('stags').child(stag);
  } else {
    stagRef = null;
  }
};

export const createStag = (props) => {

  // inject user as stag attendee
  const user = getCurrentUser();
  _set(props, `people.${user.uid}`, {
    name: user.displayName,
    email: user.email,
  });

  return db.ref('stags').push(props).then(snapshot => {
    return db.ref('users').child(getCurrentUserId()).child('stags').child(snapshot.key).set({
      created: Date.now()
    }).then(() => {
      const code = generateCode();
      return db.ref('codes').child(code).set(snapshot.key).then(codeSnapshot => {
        return {
          id: snapshot.key,
          code
        };
      });
    });
  });
};

export const joinStag = (stag) => {
  return db.ref('users').child(getCurrentUserId()).child('joined-stags').child(stag).once('value').then(snapshot => {
    const stagStatus = snapshot.val();
    if ( !stagStatus ) {
      return snapshot.ref.child('joined').set(Date.now()).then(() => {
        const user = getCurrentUser();
        return db.ref('stags').child(stag).child('people').child(getCurrentUserId()).set({
          name: user.displayName,
          email: user.email,
        });
      });
    }
    return true;
  });
};

export const getUserCreatedStags = () => {
  const user = getCurrentUserId();
  if ( user ) {
    return db.ref('users').child(getCurrentUserId()).once('value').then(snapshot => {
      const user = snapshot.val();
      return user["stags"] || {};
    });
  } else {
    return Promise.resolve({});
  }
};

export const getUserStags = () => {
  const user = getCurrentUserId();
  if ( user ) {
    // fetch the users stags, and then enrich with the stag details
    return db.ref('users').child(getCurrentUserId()).once('value').then(snapshot => {
      const user = snapshot.val();
      const stags = {
        ...user["stags"],
        ...user["joined-stags"],
      };
      const stagIds = stags ? Object.keys(stags) : [];
      return getStagSnapshots(stagIds);
    });
  } else {
    return Promise.resolve({});
  }
};

export const registerUserStagsListener = (fn) => {
  // whilst the user is authenticated, listen to updates about the users stags
  if ( typeof fn === "function" ) {
    let cb = (snapshot) => {
      const user = snapshot.val();
      const joinedStags = Object.keys(user["joined-stags"]);
      const createdStags = Object.keys(user["stags"]);
      const fetches = [];
      fetches.push(getStagSnapshots(joinedStags));
      fetches.push(getStagSnapshots(createdStags));
      Promise.all(fetches).then(vals => {
        fn(vals[0], vals[1]);
      });
    }, uid = null;
    registerAuthListener(auth => {
      if ( auth ) {
        uid = auth.uid;
        db.ref('users').child(auth.uid).on('value', cb);
      } else if ( uid && cb ) {
        db.ref('users').child(uid).off('value', cb);
        uid = null;
      }
    });
  }
  return fn;
};

export const getStagForCode = (code) => {
  return db.ref('codes').child(code).once('value').then((snapshot) => {
    return snapshot.val();
  });
};

export const getCodeForStag = (stag) => {
  return db.ref('codes').once('value').then(snapshot => {
    const codes = snapshot.val();
    let matchedCode;
    Object.keys(codes).forEach(code => {
      if ( codes[code] === stag ) {
        matchedCode = code;
      }
    });
    return matchedCode;
  });
};

export const getData = (stag) => {
  return db.ref('stags').child(stag).once('value').then((snapshot) => {
    return snapshot.val();
  });
};

export const registerPeopleListener = (fn, stag) => {
  if ( typeof fn === "function" ) {
    db.ref('stags').child(stag).child('people').orderByChild('name').on('value', snapshot => {
      fn(snapshot.val());
    });
  }
  return fn;
};
export const deregisterPeopleListener = (fn, stag) => {
  if ( fn && stag ) {
    db.ref('stags').child(stag).child('people').off('value', fn);
  }
};

export const registerAgendaListener = (fn, stag) => {
  if ( typeof fn === "function" ) {
    db.ref('stags').child(stag).child('agenda').orderByChild('date').on('value', snapshot => {
      fn(snapshot.val());
    });
  }
  return fn;
};
export const deregisterAgendaListener = (fn, stag) => {
  if ( fn && stag ) {
    db.ref('stags').child(stag).child('agenda').off('value', fn);
  }
};

export const registerHomepageListener = (fn, stag) => {
  if ( typeof fn === "function" ) {
    db.ref('stags').child(stag).child('homepage').on('value', snapshot => {
      fn(snapshot.val());
    });
  }
  return fn;
};
export const deregisterHomepageListener = (fn, stag) => {
  if ( fn && stag ) {
    db.ref('stags').child(stag).child('homepage').off('value', fn);
  }
};

export const registerPointsListener = (fn, stag) => {
  if ( typeof fn === "function" ) {
    db.ref('stags').child(stag).child('points').on('value', snapshot => {
      fn(snapshot.val());
    });
  }
  return fn;
};
export const deregisterPointsListener = (fn, stag) => {
  if ( fn && stag ) {
    db.ref('stags').child(stag).child('points').off('value', fn);
  }
};

export const registerPollsListener = (fn, stag) => {
  if ( typeof fn === "function" ) {
    db.ref('stags').child(stag).child('polls').on('value', snapshot => {
      fn(snapshot.val());
    });
  }
  return fn;
};
export const deregisterPollsListener = (fn, stag) => {
  if ( fn && stag ) {
    db.ref('stags').child(stag).child('polls').off('value', fn);
  }
};

export const updatePeople = (people) => {
  return stagRef.child('people').update(people);
};

export const updateAgenda = (agenda) => {
  return stagRef.child('agenda').update(agenda);
};

export const updateHomepage = (homepage) => {
  return stagRef.child('homepage').update(homepage);
};

export const addPointsChange = (change) => {
  return stagRef.child('points').push(change);
};

export const updateStagAttendance = (delta) => {
  return stagRef.child('people').child(getCurrentUserId()).update(delta);
};

export const setPollResponse = (pollId, choiceId, userId, response) => {
  if ( typeof response !== "undefined" ) {
    return stagRef.child('polls').child(pollId).child('responses').child(userId).child(choiceId).set(response);
  } else {
    return stagRef.child('polls').child(pollId).child('responses').child(userId).child(choiceId).set(null);
  }
};

export const uploadFile = (name, fileObj) => {
  return storage.ref().child(name).put(fileObj);
};

export const loginGoogle = () => {
  return firebase.auth().signInWithRedirect(new firebase.auth.GoogleAuthProvider());
};

export const loginFacebook = () => {
  return firebase.auth().signInWithRedirect(new firebase.auth.FacebookAuthProvider());
};

export const checkAuthStatus = () => {
  return firebase.auth().getRedirectResult().then(function(result) {
    if ( result && result.user ) {
      return result;
    } else {
      return false;
    }
  });
};

export const registerAuthListener = (fn) => {
  return firebase.auth().onAuthStateChanged(fn);
};

export const logout = () => {
  return firebase.auth().signOut().then(function() {
    // Sign-out successful.
  }).catch(function(error) {
    // An error happened.
  });
}

let timeoutId;
export const registerStatusListener = (fn) => {
  if ( typeof fn === "function" ) {
    db.ref('.info/connected').on('value', function(snapshot) {
      if (snapshot.val() === true) {
        // connected
        if ( timeoutId ) window.clearTimeout(timeoutId);
        fn(true);
      } else {
        // in 1 second, tell the app that we're disconnected
        // incase there's a quick re-connection
        timeoutId = window.setTimeout(() => {
          fn(false);
        }, config.apiOfflineWaitMs);
      }
    });
  }
};



// WEBPACK FOOTER //
// ./src/module/API/firebase.js